import { at } from './indexed';
import type { Streams } from './types';

export const MAX_GRADE = 0.3;
const GRADE_WINDOW_S = 10;
// Below this run altitude noise dominates the grade.
const MIN_RUN_M = 5;

// Minetti et al. (2002): the energy cost of running, in J/kg/m, at a grade given as rise over run.
export function minettiCost(grade: number): number {
  const i = Math.min(MAX_GRADE, Math.max(-MAX_GRADE, grade));
  return 155.4 * i ** 5 - 30.4 * i ** 4 - 43.3 * i ** 3 + 46.3 * i ** 2 + 19.5 * i + 3.6;
}

const FLAT_COST = minettiCost(0);
const valueAt = (values: readonly (number | null)[] | undefined, i: number): number | null => values?.[i] ?? null;

function distances(t: readonly number[], speed: readonly (number | null)[] | undefined): number[] {
  const d = [0];
  for (let i = 1; i < t.length; i++) {
    const dt = at(t, i) - at(t, i - 1);
    d.push(at(d, i - 1) + (valueAt(speed, i) ?? 0) * dt);
  }
  return d;
}

// Rise over run back to the first sample inside the previous 10 s; flat when that run is under 5 m or an altitude is missing.
function grades(t: readonly number[], altitude: readonly (number | null)[] | undefined, d: readonly number[]): number[] {
  const out: number[] = [];
  let k = 0;
  for (let i = 0; i < t.length; i++) {
    while (at(t, k) < at(t, i) - GRADE_WINDOW_S) k++;
    const top = valueAt(altitude, i);
    const bottom = valueAt(altitude, k);
    const run = at(d, i) - at(d, k);
    out.push(top === null || bottom === null || run < MIN_RUN_M ? 0 : (top - bottom) / run);
  }
  return out;
}

// The flat speed that costs the same energy; null where the athlete isn't moving.
export function gradeAdjustedSpeeds(streams: Pick<Streams, 't' | 'speed' | 'altitude'>): (number | null)[] {
  const d = distances(streams.t, streams.speed);
  const g = grades(streams.t, streams.altitude, d);
  return streams.t.map((_, i) => {
    const speed = valueAt(streams.speed, i);
    return speed === null || speed <= 0 ? null : (speed * minettiCost(at(g, i))) / FLAT_COST;
  });
}
